import { useCallback, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import FormFieldVersionInput from "components/FormFields/VersionInput";
import VersionTooltipContent from "components/FormFields/VersionInput/VersionTooltipContent";
import { VERSION_INPUT_TYPE, VersionInputOption } from "components/FormFields/VersionInput/types";
import FormFooterEdit from "components/Forms/FooterEdit";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { TERRAGRUNT_TOOL_OPTIONS } from "constants/terragruntTools";
import { VENDOR_DICTIONARY, VENDOR_KEY } from "constants/vendor";
import Badge from "ds/components/Badge";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import useGetVendorResources from "shared/Stack/Vendor/useGetVendorResources";
import { TerragruntTool } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";
import Card from "ds/components/Card";

import { StackSettingsContextData } from "../../Context";
import StackSettingsViewVendorFormField from "../../components/ViewVendorFormField";
import { StackSettingsVendorComponentBaseProps } from "../types";
import { getToolTooltip } from "./getTooltips";
import {
  VERSION_EMPTY_VALUE,
  getFormDefaultValues,
  getValidateTerragruntVersion,
  getValidateToolVersion,
  mapCreateStackVendorUpdateInput,
} from "./helpers";

export type StackSettingsTerragruntVendorFormFields = {
  terragruntVersion: VersionInputOption;
  tool: TerragruntTool;
  toolVersion: VersionInputOption;
  useSmartSanitization: boolean;
  useRunAll: boolean;
  skipReplan: boolean;
  useStateManagement: boolean;
  prefixResourceNamesWithModuleName: boolean;
};

type StackSettingsVendorTerragruntEditProps = StackSettingsVendorComponentBaseProps;

const StackSettingsVendorTerragruntEdit = ({
  onStackUpdate,
  updateLoading,
}: StackSettingsVendorTerragruntEditProps) => {
  const { terragruntVersions, terraformVersions, openTofuVersions, isRefetching, refetch } =
    useGetVendorResources({
      vendorSpecific: VENDOR_KEY.TERRAGRUNT,
    });
  const { stackSettings } = useTypedContext(StackSettingsContextData);
  const terragruntVendorForm = useForm<StackSettingsTerragruntVendorFormFields>({
    defaultValues: getFormDefaultValues(stackSettings),
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
    watch,
    setValue,
    trigger,
  } = terragruntVendorForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const watchedTerragruntTool = watch("tool");
  const watchedTerragruntToolVersion = watch("toolVersion");
  const watchedUseRunAll = watch("useRunAll");
  const isManualMode = watchedTerragruntTool === TerragruntTool.ManuallyProvisioned;
  const workflowVersions =
    watchedTerragruntTool === TerragruntTool.OpenTofu ? openTofuVersions : terraformVersions;
  const vendorToolName =
    watchedTerragruntTool === TerragruntTool.OpenTofu
      ? VENDOR_DICTIONARY.OPEN_TOFU
      : VENDOR_DICTIONARY.TERRAFORM;

  const handleToolChange = useCallback(
    (onFieldChange: (value: TerragruntTool) => void) => (value: TerragruntTool) => {
      if (watchedTerragruntTool === value) {
        return undefined;
      }

      let versionOption: VersionInputOption = VERSION_EMPTY_VALUE;
      if (value === TerragruntTool.TerraformFoss) {
        versionOption = {
          value: terraformVersions ? terraformVersions[0] : "",
          type: watchedTerragruntToolVersion.type || VERSION_INPUT_TYPE.SPECIFIC,
        };
      } else if (value === TerragruntTool.OpenTofu) {
        versionOption = {
          value: openTofuVersions ? openTofuVersions[0] : "",
          type: watchedTerragruntToolVersion.type || VERSION_INPUT_TYPE.SPECIFIC,
        };
      }

      setValue("toolVersion", versionOption);
      onFieldChange(value);

      trigger();
    },
    [
      openTofuVersions,
      setValue,
      terraformVersions,
      trigger,
      watchedTerragruntTool,
      watchedTerragruntToolVersion?.type,
    ]
  );

  const onSubmit = (formData: StackSettingsTerragruntVendorFormFields) => {
    onStackUpdate(mapCreateStackVendorUpdateInput(formData));
  };

  useEffect(() => {
    reset(getFormDefaultValues(stackSettings));
  }, [reset, stackSettings]);

  const toolController = (
    <Controller
      name="tool"
      control={control}
      render={({ field, fieldState }) => (
        <FormField error={fieldState.error?.message} noMargin label="Tool" {...getToolTooltip()}>
          {({ ariaInputProps }) => (
            <Select
              value={field.value}
              items={TERRAGRUNT_TOOL_OPTIONS}
              onChange={handleToolChange(field.onChange)}
              isError={!!fieldState.error}
              {...ariaInputProps}
            />
          )}
        </FormField>
      )}
    />
  );

  const toolVersionController = (
    <Controller
      name="toolVersion"
      control={control}
      rules={{
        validate: getValidateToolVersion(workflowVersions, vendorToolName),
      }}
      render={({ field, fieldState }) => {
        const version = field.value;
        return (
          <FormFieldVersionInput
            reloadLoading={isRefetching}
            reloadVersionsData={refetch}
            title={`${vendorToolName} version`}
            tooltipInfo={
              <>
                <TooltipModalTitle>Choose version</TooltipModalTitle>
                <TooltipModalBody align="start">
                  <VersionTooltipContent />
                  <ReadMoreDocsLink docsUrl={getDocsUrl("/vendors/terragrunt/terragrunt-tool")} />
                </TooltipModalBody>
              </>
            }
            errorMessage={fieldState.error?.message}
            type={version.type}
            version={version.value}
            supportedVersions={workflowVersions}
            onChange={field.onChange}
          />
        );
      }}
    />
  );

  return (
    <>
      <StackSettingsViewVendorFormField vendorName={VENDOR_DICTIONARY.TERRAGRUNT} />

      <Controller
        name="terragruntVersion"
        control={control}
        rules={{
          validate: getValidateTerragruntVersion(terragruntVersions),
        }}
        render={({ field, fieldState }) => {
          const version = field.value;
          return (
            <FormFieldVersionInput
              title="Terragrunt version"
              tooltipInfo={
                <>
                  <TooltipModalTitle>Choose version</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    <VersionTooltipContent />
                  </TooltipModalBody>
                </>
              }
              reloadLoading={isRefetching}
              reloadVersionsData={refetch}
              errorMessage={fieldState.error?.message}
              type={version.type}
              version={version.value}
              supportedVersions={terragruntVersions}
              onChange={field.onChange}
            />
          );
        }}
      />

      {isManualMode && toolController}
      {!isManualMode && (
        <Card contentGap="x-large">
          {toolController}
          {toolVersionController}
        </Card>
      )}

      <Controller
        name="useSmartSanitization"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={(arg) => {
              field.onChange(arg);
              trigger("toolVersion");
            }}
            checked={field.value}
            title="Smart sanitization (recommended)"
            description='If set, Spacelift will attempt to sanitize the resources created by terraform using the information provided by terraform as to which fields are "sensitive" or not'
          />
        )}
      />

      <Controller
        name="useRunAll"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={(value: boolean) => {
              field.onChange(value);
              setValue("prefixResourceNamesWithModuleName", value);
            }}
            checked={field.value}
            title="Use All Run"
            description={`Enable this property to use Terragrunt's "run-all" capabilities to execute commands across all projects in the workspace.`}
          />
        )}
      />

      <Controller
        name="skipReplan"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={field.onChange}
            checked={field.value}
            title="Skip replan"
            titleSuffix={<Badge variant="blue" type="strong" text="BETA" />}
            description="Skip the second planning phase during the apply stage by reusing the saved plan. Applies to both Run All and non-Run All stacks. Warning: this means any mocked_outputs referenced during planning will be applied as-is — do not enable this together with mocked_outputs unless you fully understand the implications, your apply may execute against mocked values rather than real ones."
          />
        )}
      />

      <Controller
        name="prefixResourceNamesWithModuleName"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={field.onChange}
            checked={field.value}
            disabled={watchedUseRunAll}
            title="Prefix resource names with module path"
            description="When enabled, resource and output names are prefixed with the module path. Always on when Use All Run is enabled."
          />
        )}
      />

      <Controller
        name="useStateManagement"
        control={control}
        render={({ field }) => (
          <FormToggleField
            variant="switch"
            align="center"
            onChange={field.onChange}
            checked={field.value}
            title="Manage state"
            titleSuffix={<Badge variant="blue" type="strong" text="BETA" />}
            description="Indicates whether Spacelift should take care of the Terragrunt state. Cannot be changed after the stack is created."
            disabled
          />
        )}
      />

      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(stackSettings))}
        isDirty={isDirty}
        submitLoading={updateLoading}
      />
    </>
  );
};

export default StackSettingsVendorTerragruntEdit;
